import { apiClient } from "@/lib/api/client";
import { normalizeAuthEnvelope, unwrapApiData } from "@/lib/api/response";
import type {
  AuthResponse,
  ActiveContextResponse,
  CreateFunctionalAccountPayload,
  CreateFunctionalAccountResponse,
  CurrentSessionResponse,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  FunctionalAccountsResponse,
  LoginPayload,
  RefreshResponse,
  RegisterPayload,
  ResetPasswordPayload,
  ResetPasswordResponse,
  SelectProfilePayload,
  SelectProfileResponse,
  SwitchActiveContextResponse,
  SwitchFunctionalAccountPayload,
  SwitchOrganizationPayload,
} from "@/features/auth/types";

/**
 * Ben login per perdoruesin dhe kthen token pair nga backend-i.
 */
export const login = async (payload: LoginPayload): Promise<AuthResponse> => {
  const response = await apiClient.post<unknown>("/auth/login", payload);

  if (import.meta.env.DEV) {
    console.debug("Login Response:", response.data);
  }

  return normalizeAuthEnvelope<AuthResponse>(response.data, "Login");
};

/**
 * Regjistron llogari te re dhe kthen token pair per hyrje direkte.
 */
export const registerAccount = async (payload: RegisterPayload): Promise<AuthResponse> => {
  const response = await apiClient.post<unknown>("/auth/register", payload);

  return normalizeAuthEnvelope<AuthResponse>(response.data, "Register");
};

/**
 * Revokon refresh token ne backend.
 */
export const logout = async (refreshToken: string): Promise<void> => {
  await apiClient.post("/auth/logout", { refresh_token: refreshToken });
};

/**
 * Rrotullon refresh token-in dhe kthen token pair te ri.
 */
export const refreshSession = async (refreshToken: string): Promise<RefreshResponse> => {
  const response = await apiClient.post<unknown>("/auth/refresh", {
    refresh_token: refreshToken,
  });

  return normalizeAuthEnvelope<RefreshResponse>(response.data, "Refresh");
};

/**
 * Verifikon access token-in aktual dhe kthen identitetin minimal nga JWT.
 */
export const getCurrentSession = async (): Promise<CurrentSessionResponse> => {
  const response = await apiClient.get<unknown>("/auth/me");

  return unwrapApiData<CurrentSessionResponse>(response.data);
};

/**
 * Nis rrjedhen publike te harrimit te fjalekalimit.
 */
export const forgotPassword = async (payload: ForgotPasswordPayload): Promise<ForgotPasswordResponse> => {
  const response = await apiClient.post<unknown>("/auth/forgot-password", payload);

  return unwrapApiData<ForgotPasswordResponse>(response.data);
};

/**
 * Kryen reset te fjalekalimit me token nga URL.
 */
export const resetPassword = async (payload: ResetPasswordPayload): Promise<ResetPasswordResponse> => {
  const response = await apiClient.post<unknown>("/auth/reset-password", payload);

  return unwrapApiData<ResetPasswordResponse>(response.data);
};

/**
 * Vendos profilin aktiv pas regjistrimit fillestar.
 */
export const selectActiveProfile = async (payload: SelectProfilePayload): Promise<SelectProfileResponse> => {
  const response = await apiClient.post<unknown>("/auth/select-profile", payload);

  return unwrapApiData<SelectProfileResponse>(response.data);
};

export const getFunctionalAccounts = async (): Promise<FunctionalAccountsResponse> => {
  const response = await apiClient.get<unknown>("/auth/accounts");

  return unwrapApiData<FunctionalAccountsResponse>(response.data);
};

export const createFunctionalAccount = async (
  payload: CreateFunctionalAccountPayload,
): Promise<CreateFunctionalAccountResponse> => {
  const response = await apiClient.post<unknown>("/auth/accounts", payload);

  return unwrapApiData<CreateFunctionalAccountResponse>(response.data);
};

export const getActiveContext = async (): Promise<ActiveContextResponse> => {
  const response = await apiClient.get<unknown>("/auth/context");

  return unwrapApiData<ActiveContextResponse>(response.data);
};

export const switchFunctionalAccount = async (
  payload: SwitchFunctionalAccountPayload,
): Promise<SwitchActiveContextResponse> => {
  const response = await apiClient.post<unknown>("/auth/switch-account", payload);

  return unwrapApiData<SwitchActiveContextResponse>(response.data);
};

export const switchOrganization = async (
  payload: SwitchOrganizationPayload,
): Promise<SwitchActiveContextResponse> => {
  const response = await apiClient.post<unknown>("/auth/switch-organization", payload);

  return unwrapApiData<SwitchActiveContextResponse>(response.data);
};
